import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "parties", title: "Parties & Scope" },
  { id: "definitions", title: "Definitions" },
  { id: "roles", title: "Roles of the Parties" },
  { id: "processing", title: "Details of Processing" },
  { id: "obligations", title: "Processor Obligations" },
  { id: "subprocessors", title: "Sub-processors" },
  { id: "security", title: "Security Measures" },
  { id: "transfers", title: "International Transfers" },
  { id: "rights", title: "Data Subject Rights" },
  { id: "breach", title: "Personal Data Breach" },
  { id: "audits", title: "Audits & Information" },
  { id: "term", title: "Term, Return & Deletion" },
  { id: "liability", title: "Liability & Governing Law" },
  { id: "signatures", title: "Signatures" },
];

const DataProcessingAgreement = () => (
  <>
    <Helmet>
      <title>Data Processing Agreement — The Emerging Market</title>
      <meta
        name="description"
        content="Standard Data Processing Agreement (DPA) for agencies, funders, and partners working with The Emerging Market."
      />
      <link rel="canonical" href="https://emrkt.co/dpa" />
    </Helmet>
    <LegalLayout
      title="Data Processing Agreement"
      description="Template DPA governing processing of personal data by The Emerging Market on behalf of agency, funder, and partner customers."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="parties">
        <h2>1. Parties & Scope</h2>
        <p>
          This Data Processing Agreement ("DPA") is entered into between the customer
          identified in the underlying services agreement ("Customer," "Controller") and
          The Emerging Market, an Alabama entity operating emrkt.co ("TEM," "Processor").
          It applies whenever TEM processes Personal Data on Customer's behalf in
          connection with the Service.
        </p>
        <p>
          This DPA is incorporated into and forms part of the Customer's Master Services
          Agreement, Order Form, or <a href="/terms">Terms of Service</a> with TEM (the "Agreement"). If there
          is a conflict between this DPA and the Agreement with respect to Personal Data,
          this DPA controls.
        </p>
      </section>

      <section id="definitions">
        <h2>2. Definitions</h2>
        <p>
          Capitalized terms not defined here have the meanings given in the GDPR, UK GDPR,
          or CCPA/CPRA, as applicable.
        </p>
        <ul>
          <li><strong>"Personal Data"</strong> means information about an identified or identifiable natural person that Customer submits to the Service.</li>
          <li><strong>"Processing"</strong> means any operation performed on Personal Data, including collection, storage, use, disclosure, and deletion.</li>
          <li><strong>"Sub-processor"</strong> means any third party engaged by TEM to process Personal Data on behalf of Customer.</li>
          <li><strong>"Applicable Data Protection Laws"</strong> means all laws governing the processing of Personal Data applicable to the parties, including GDPR, UK GDPR, CCPA/CPRA, and US state privacy laws.</li>
        </ul>
      </section>

      <section id="roles">
        <h2>3. Roles of the Parties</h2>
        <p>
          For Personal Data submitted by Customer through the Service, Customer is the
          Controller and TEM is the Processor. Where US state privacy laws apply, TEM
          acts as a Service Provider or Processor and not as a Third Party. TEM will
          process Personal Data only on documented instructions from Customer, which
          include the Agreement, this DPA, and Customer's use of the Service.
        </p>
      </section>

      <section id="processing">
        <h2>4. Details of Processing</h2>
        <ul>
          <li><strong>Subject matter:</strong> TEM's provision of the Service to Customer.</li>
          <li><strong>Duration:</strong> the term of the Agreement plus any post-termination return/deletion period.</li>
          <li><strong>Nature & purpose:</strong> hosting, displaying, transmitting, and analyzing Customer content; account management; communications; and product improvement.</li>
          <li><strong>Categories of data subjects:</strong> Customer's authorized users, contacts, partners, and individuals whose information Customer submits.</li>
          <li><strong>Categories of Personal Data:</strong> identifiers (name, email), professional information, organization affiliation, account credentials, IP address, device and usage data, and any data Customer chooses to upload.</li>
          <li><strong>Special categories:</strong> not expected. Customer agrees not to submit special-category data unless specifically agreed in writing.</li>
        </ul>
      </section>

      <section id="obligations">
        <h2>5. Processor Obligations</h2>
        <ul>
          <li>Process Personal Data only on Customer's documented instructions, including with regard to international transfers.</li>
          <li>Ensure personnel authorized to process Personal Data are bound by confidentiality obligations.</li>
          <li>Implement and maintain the technical and organizational measures described in Section 7.</li>
          <li>Assist Customer, taking into account the nature of processing, in responding to data subject requests and in meeting Customer's obligations under Articles 32–36 of the GDPR.</li>
          <li>Notify Customer if, in TEM's opinion, an instruction infringes Applicable Data Protection Laws.</li>
        </ul>
      </section>

      <section id="subprocessors">
        <h2>6. Sub-processors</h2>
        <p>
          Customer grants TEM general authorization to engage Sub-processors to support
          the Service. TEM remains responsible for its Sub-processors' performance under
          this DPA and will impose data protection terms substantially similar to those
          in this DPA on each Sub-processor.
        </p>
        <p>
          A current list of Sub-processors (including cloud hosting, email delivery,
          error monitoring, analytics, and AI providers) is available on request and is
          referenced in our <a href="/privacy">Privacy Policy</a>. TEM will provide at
          least 30 days' prior notice of new Sub-processors via email or in-product
          notification. Customer may object on reasonable data protection grounds.
        </p>
      </section>

      <section id="security">
        <h2>7. Security Measures</h2>
        <p>
          TEM implements appropriate technical and organizational measures designed to
          protect Personal Data against unauthorized or unlawful processing and against
          accidental loss, destruction, damage, alteration, or disclosure. These include:
        </p>
        <ul>
          <li>Encryption in transit (TLS 1.2+) and at rest for production databases and backups.</li>
          <li>Role-based access controls, least-privilege principles, and row-level security on multi-tenant data.</li>
          <li>Multi-factor authentication for administrative access.</li>
          <li>Centralized logging, error monitoring, and uptime monitoring with alerting.</li>
          <li>Daily backups with a documented restore drill cadence.</li>
          <li>Vulnerability monitoring of dependencies and timely patching.</li>
          <li>Onboarding and ongoing security awareness for personnel with access to production.</li>
        </ul>
        <p>
          A plain-language summary of these controls, our incident-response runbook, and
          vulnerability-reporting instructions is published at{" "}
          <a href="/security">/security</a>.
        </p>
      </section>

      <section id="transfers">
        <h2>8. International Transfers</h2>
        <p>
          The Service is hosted in the United States. Where Customer transfers Personal
          Data of EEA, UK, or Swiss data subjects to TEM, the parties agree that the
          EU Standard Contractual Clauses (Module Two: Controller to Processor) and the
          UK International Data Transfer Addendum are incorporated by reference and
          form an integral part of this DPA. Annexes are populated by the details in
          Section 4 and Section 7.
        </p>
      </section>

      <section id="rights">
        <h2>9. Data Subject Rights</h2>
        <p>
          Taking into account the nature of the processing, TEM will provide reasonable
          assistance through appropriate technical and organizational measures to enable
          Customer to respond to requests from data subjects exercising their rights
          under Applicable Data Protection Laws (access, rectification, erasure,
          restriction, portability, and objection). Where TEM receives a request
          directly, it will promptly forward it to Customer without responding to the
          substance, unless legally required to do so.
        </p>
      </section>

      <section id="breach">
        <h2>10. Personal Data Breach</h2>
        <p>
          TEM will notify Customer without undue delay, and where feasible within 72
          hours, after becoming aware of a Personal Data breach affecting Customer's
          Personal Data. The notification will describe, to the extent known: the nature
          of the breach, categories and approximate number of data subjects and records
          concerned, likely consequences, and measures taken or proposed to address the
          breach and mitigate its effects.
        </p>
      </section>

      <section id="audits">
        <h2>11. Audits & Information</h2>
        <p>
          TEM will make available to Customer information reasonably necessary to
          demonstrate compliance with this DPA. Customer's audit rights are satisfied
          by TEM providing, on written request, copies of relevant third-party audit
          reports, certifications (e.g., SOC 2 when available), and responses to a
          reasonable security questionnaire no more than once per twelve-month period,
          subject to confidentiality. On-site audits may be conducted only where
          required by Applicable Data Protection Laws, with reasonable prior notice,
          during business hours, and at Customer's expense.
        </p>
      </section>

      <section id="term">
        <h2>12. Term, Return & Deletion</h2>
        <p>
          This DPA remains in effect for as long as TEM processes Personal Data on
          Customer's behalf. On termination of the Agreement, TEM will, at Customer's
          choice, return or delete Personal Data in accordance with the timelines in our{" "}
          <a href="/retention">Records Retention & Deletion Policy</a>, except to the
          extent retention is required by law. Backups are overwritten on their normal
          rolling cycle.
        </p>
      </section>

      <section id="liability">
        <h2>13. Liability & Governing Law</h2>
        <p>
          Each party's liability under this DPA is subject to the limitations and
          exclusions of liability set out in the Agreement. This DPA is governed by the
          governing law specified in the Agreement, except where Applicable Data
          Protection Laws require otherwise.
        </p>
      </section>

      <section id="signatures">
        <h2>14. Signatures</h2>
        <p>
          Execution is not required where this DPA is incorporated by reference into a
          signed Agreement or accepted electronically as part of the Service's Terms of
          Service. Customers requiring a counter-signed copy may email{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> with their entity name,
          jurisdiction, and signatory details.
        </p>
        <p className="text-xs">
          This template is provided for transparency and procurement convenience. It
          does not constitute legal advice; both parties are encouraged to have counsel
          review before execution.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default DataProcessingAgreement;